import { createSelector } from '@reduxjs/toolkit';

import type { ElasticVehicle } from '../@types/ElasticVehicle';
import type { ParsedBranding } from '../@types/branding';
import type Item from '../@types/item';
import Branding from '../models/branding';
import type { RootState } from '../reducers';
import { getQueryFilter } from '../utils/reselectors';
import { getItem } from './item';

const MANUFACTURER_PACKAGE_CATEGORY = 'Manufacture packaging';
const MRF_PACKAGE_CATEGORY = 'MRF packaging';

const getManufacturePackagings = createSelector(
  (state: RootState) => state.brandings.list,
  (list) => (list || []).filter((b) => b.category.includes(MANUFACTURER_PACKAGE_CATEGORY)),
);

const getMrfPackagings = createSelector(
  (state: RootState) => state.brandings.list,
  (list) => (list || []).filter((b) => b.category.includes(MRF_PACKAGE_CATEGORY)),
);

export const getItemManufacturerPackagings = createSelector(
  getManufacturePackagings,
  (_: RootState, item: Item | ElasticVehicle) => item,
  (packagings, item) => {
    if (!item || !item.hasManufacturerPackaging) {
      return [];
    }

    return packagings.filter((p) => p.identifier === item.manufacturer).map(Branding.parse);
  },
);

export const getManufacturePackagingForCurrentItem = createSelector(
  getItem,
  getManufacturePackagings,
  (item, packagings) => {
    if (!item || !item.hasManufacturerPackaging) {
      return null;
    }
    const { manufacturer } = item;
    const result = packagings.filter((p) => p.identifier === manufacturer).map(Branding.parse);
    return result.length ? result.pop() : null;
  },
);

export const getMrfPackagingForCurrentItem = createSelector(
  getItem,
  getMrfPackagings,
  (item, packagings) => {
    if (!item || !item.hasMrfPackaging) {
      return [];
    }

    return packagings;
  },
);

export const getFirstItemManufacturerPackaging = createSelector(
  [getItemManufacturerPackagings],
  (brandings) => brandings?.[0] || null,
);

const isSameBrand = (identifier: string, manufacturer?: string) => {
  if (!identifier || !manufacturer) {
    return false;
  }

  return identifier.toLowerCase() === manufacturer.toLowerCase();
};

export const getSearchManufacturePackaging = createSelector(
  getManufacturePackagings,
  getQueryFilter,
  (packagings, queryFilter) => {
    if (queryFilter && (queryFilter.get('manufacturer') || []).length === 1) {
      const manufacturer = queryFilter.get('manufacturer').pop();
      const _package = packagings.find((p) => isSameBrand(p.identifier, manufacturer));

      if (_package) return Branding.parse(_package);
    }
    return null;
  },
);

export const getBrandingList = createSelector(
  (state: RootState) => state.brandings.list,
  (list): ParsedBranding[] => (list || []).map(Branding.parse),
);

export const shouldFetchBrandings = createSelector(
  getBrandingList,
  (brandings) => brandings.length === 0,
);

export const hasBrandings = (state: RootState) => getBrandingList(state).length > 0;
